import React from 'react'
import { UserService, PartnerService, ChallengeService } from '@duck/api-definitions'
import { TranslationKey } from '@/modules/i18n'
import type { ColumnDef, FilterType, EnumFilterOption } from './generalTable.utils'
import { PartnerTranslationKey } from '../usePartnerTar'

export type CustomStatus = 'added' | 'assignmentSent' | 'joined' | 'started' | 'submitted' | 'forfeited'

export interface PartnerDashboardRow {
  personId: string
  assignmentId: string
  taskId: string
  taskDisplayName: string
  email: string
  onboardingStatus: UserService.PersonOnboardingStatusEnum
  assignmentStatus?: PartnerService.AssignmentDetailsStatus
  customStatus: CustomStatus
  grade?: number
  feedback?: string
  solution?: string
  startDate?: string
  submitDate?: string
  workTime?: number
  challengeId?: string
  charts?: PartnerService.AssignmentDetails['charts']
  queries?: PartnerService.AssignmentDetails['queries']
  chosenWeapon?: ChallengeService.TaskWeaponsOfChoiceEnum
  jNotebook?: PartnerService.AssignmentDetails['jNotebook']
  assignmentSentDate?: string
  cohortName?: string
}

export type DashboardColumnKey =
  | 'personId'
  | 'email'
  | 'taskDisplayName'
  | 'customStatus'
  | 'grade'
  | 'assignmentSentDate'
  | 'cohortName'

export interface DashboardColumnDef extends ColumnDef {
  key: DashboardColumnKey
  label?: TranslationKey
  partnerLabel?: PartnerTranslationKey
  filterType: FilterType
}

export const allDashboardColumns: DashboardColumnDef[] = [
  { key: 'personId', label: 'common.name', filterType: 'text' },
  { key: 'email', label: 'common.email', filterType: 'text' },
  { key: 'customStatus', label: 'common.status', filterType: 'enum' },
  { key: 'grade', label: 'common.grade', filterType: 'text' },
  { key: 'taskDisplayName', label: 'challenges.challenge', filterType: 'text' },
  { key: 'assignmentSentDate', label: 'common.assignmentSent', filterType: 'text' },
  { key: 'cohortName', partnerLabel: 'cohort', filterType: 'text' },
]

const customStatusPrecedence: Record<CustomStatus, number> = {
  added: 0,
  assignmentSent: 1,
  joined: 2,
  started: 3,
  submitted: 4,
  forfeited: 5,
}

const assignmentStatusToCustomStatus: Record<PartnerService.AssignmentDetailsStatus, CustomStatus> = {
  [PartnerService.AssignmentDetailsStatus.Forfeited]: 'forfeited',
  [PartnerService.AssignmentDetailsStatus.Submitted]: 'submitted',
  [PartnerService.AssignmentDetailsStatus.Started]: 'started',
  [PartnerService.AssignmentDetailsStatus.Sent]: 'assignmentSent',
}

export const getCustomStatus = (
  onboardingStatus: UserService.PersonOnboardingStatusEnum,
  assignmentStatus?: PartnerService.AssignmentDetailsStatus,
): CustomStatus => {
  if (assignmentStatus) {
    const mapped = assignmentStatusToCustomStatus[assignmentStatus]
    if (mapped) return mapped
  }

  const onboardingToCustomStatus: Record<UserService.PersonOnboardingStatusEnum, CustomStatus> = {
    [UserService.PersonOnboardingStatusEnum.Joined]: 'joined',
    [UserService.PersonOnboardingStatusEnum.Invited]: 'assignmentSent',
    [UserService.PersonOnboardingStatusEnum.Added]: 'added',
  }

  return onboardingToCustomStatus[onboardingStatus] ?? 'added'
}

const customStatusBadgeColorMap: Record<CustomStatus, string> = {
  added: 'bg-secondary/10 text-secondary border border-secondary/20',
  assignmentSent: 'bg-blue-500/10 text-blue-600 border border-blue-500/20',
  joined: 'bg-success/10 text-success border border-success/20',
  started: 'bg-warning/10 text-warning border border-warning/20',
  submitted: 'bg-success/10 text-success border border-success/20',
  forfeited: 'bg-destructive/10 text-destructive border border-destructive/20',
}

export const getCustomStatusBadgeColor = (status: CustomStatus): string =>
  customStatusBadgeColorMap[status] ?? 'bg-muted/10 text-muted-foreground border border-border'

const customStatusLabelMap: Record<CustomStatus, TranslationKey> = {
  added: 'common.added',
  assignmentSent: 'common.assignmentSentStatus',
  joined: 'common.joined',
  started: 'common.started',
  submitted: 'common.submitted',
  forfeited: 'common.forfeited',
}

export const formatCustomStatus = (status: CustomStatus): TranslationKey =>
  customStatusLabelMap[status] ?? 'common.unknown'

export const getCustomStatusOptions = (t: (key: TranslationKey) => string): EnumFilterOption[] =>
  (['added', 'assignmentSent', 'joined', 'started', 'submitted', 'forfeited'] as const).map(
    (status) => ({
      value: status,
      label: customStatusLabelMap[status],
      component: (
        <span
          className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getCustomStatusBadgeColor(status)}`}
        >
          {t(customStatusLabelMap[status])}
        </span>
      ),
    })
  )

export const sortPartnerDashboardRows = (
  rows: PartnerDashboardRow[],
  persons: UserService.Person[],
  sortColumn: string | null,
  sortDirection: 'asc' | 'desc' | null,
): PartnerDashboardRow[] => {
  if (!sortColumn || !sortDirection) return rows

  return [...rows].sort((a, b) => {
    let comparison = 0

    if (sortColumn === 'personId') {
      const personA = persons.find((p) => p.id === a.personId)
      const personB = persons.find((p) => p.id === b.personId)
      const nameA = `${personA?.firstName || ''} ${personA?.lastName || ''}`.trim()
      const nameB = `${personB?.firstName || ''} ${personB?.lastName || ''}`.trim()
      comparison = (nameA || a.personId).localeCompare(nameB || b.personId)
    } else if (sortColumn === 'customStatus') {
      comparison = (customStatusPrecedence[a.customStatus] ?? 99) - (customStatusPrecedence[b.customStatus] ?? 99)
    } else if (sortColumn === 'grade') {
      const aGrade = a.grade ?? -1
      const bGrade = b.grade ?? -1
      comparison = aGrade - bGrade
    } else if (sortColumn === 'taskDisplayName') {
      comparison = (a.taskDisplayName || '').localeCompare(b.taskDisplayName || '')
    } else if (sortColumn === 'email') {
      comparison = (a.email || '').localeCompare(b.email || '')
    } else if (sortColumn === 'assignmentSentDate') {
      const aDate = a.assignmentSentDate ? new Date(a.assignmentSentDate).getTime() : 0
      const bDate = b.assignmentSentDate ? new Date(b.assignmentSentDate).getTime() : 0
      comparison = aDate - bDate
    } else if (sortColumn === 'cohortName') {
      comparison = (a.cohortName || '').localeCompare(b.cohortName || '')
    }

    return sortDirection === 'asc' ? comparison : -comparison
  })
}

export const filterPartnerDashboardRows = (
  rows: PartnerDashboardRow[],
  persons: UserService.Person[],
  availableColumns: DashboardColumnDef[],
  getColumnFilter: (columnKey: string) => string,
  getColumnFilterArray: (columnKey: string) => string[],
): PartnerDashboardRow[] => {
  let filtered = rows

  availableColumns.forEach((col) => {
    if (col.filterType === 'enum') {
      const filterValues = getColumnFilterArray(col.key)
      if (filterValues.length === 0) return

      const filterSet = new Set(filterValues)
      filtered = filtered.filter((item) => filterSet.has(item[col.key] as string))
    } else {
      const filterValue = getColumnFilter(col.key)
      if (!filterValue.trim()) return

      const searchTerm = filterValue.toLowerCase()
      filtered = filtered.filter((item) => {
        if (col.key === 'personId') {
          const person = persons.find((p) => p.id === item.personId)
          const fullName = `${person?.firstName || ''} ${person?.lastName || ''}`.trim()
          return fullName.toLowerCase().includes(searchTerm)
        }

        const value = item[col.key as keyof PartnerDashboardRow]
        if (value === null || value === undefined) return false
        return String(value).toLowerCase().includes(searchTerm)
      })
    }
  })

  return filtered
}
